import type { JSX } from 'react';
import type { UseFormRegister, FieldErrors } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CountrySelect } from '@/components/ui/country-select';
import { CompanySizeSelect } from './CompanySizeSelect';
import type { CreateCompanyFormData } from '../schemas/company.schemas';

export interface CompanyFormFieldsProps {
  register: UseFormRegister<CreateCompanyFormData>;
  errors: FieldErrors<CreateCompanyFormData>;
  onSizeChange: (value: CreateCompanyFormData['companySize']) => void;
  onCountryChange: (value: string) => void;
  countryValue: string;
  sizeValue: string | null | undefined;
}

function BasicInfoFields({
  register,
  errors,
}: Pick<CompanyFormFieldsProps, 'register' | 'errors'>): React.JSX.Element {
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="name">Name *</Label>
        <Input id="name" {...register('name')} />
        {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="email">Email *</Label>
        <Input id="email" type="email" {...register('email')} />
        {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone">Phone</Label>
        <Input id="phone" {...register('phone')} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="taxId">Tax Number</Label>
        <Input id="taxId" {...register('taxId')} />
        {errors.taxId && <p className="text-sm text-red-500">{errors.taxId.message}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="industry">Industry</Label>
        <Input id="industry" {...register('industry')} />
      </div>
    </>
  );
}

function LocationFields({
  register,
  onSizeChange,
  onCountryChange,
  countryValue,
  sizeValue,
}: Omit<CompanyFormFieldsProps, 'errors'>): React.JSX.Element {
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="companySize">Company Size</Label>
        <CompanySizeSelect value={sizeValue} onChange={onSizeChange} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="country">Country</Label>
        <CountrySelect value={countryValue} onChange={onCountryChange} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="city">City</Label>
        <Input id="city" {...register('city')} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="address">Address</Label>
        <Input id="address" {...register('address')} />
      </div>
    </>
  );
}

export function CompanyFormFields({
  register,
  errors,
  onSizeChange,
  onCountryChange,
  countryValue,
  sizeValue,
}: CompanyFormFieldsProps): JSX.Element {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <BasicInfoFields register={register} errors={errors} />
      <LocationFields
        register={register}
        onSizeChange={onSizeChange}
        onCountryChange={onCountryChange}
        countryValue={countryValue}
        sizeValue={sizeValue}
      />
    </div>
  );
}
